import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Phone, ArrowRight, User, Mail, Calendar, Users } from 'lucide-react';
import { trackEvent } from '../context/SiteContext';
import { trackWhatsAppConversion } from '../utils/consent';
import { useSiteContent } from '../context/SiteContext';
import './Chatbot.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getSessionId = () => {
  let sid = sessionStorage.getItem('chatbot_session_id');
  if (!sid) {
    sid = 'chat_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    sessionStorage.setItem('chatbot_session_id', sid);
  }
  return sid;
};

const Chatbot = () => {
  const { settings } = useSiteContent();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', eventType: '', eventDate: '', guestCount: '' });
  const [formSent, setFormSent] = useState(false);
  const [formConsent, setFormConsent] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const toggleOpen = () => {
    if (!isOpen) {
      trackEvent('chatbot_open');
      if (messages.length === 0) {
        setMessages([{ role: 'assistant', content: 'Hallo! Willkommen bei der BD Event Location. Wie kann ich Ihnen weiterhelfen? Planen Sie eine Hochzeit, Firmenfeier oder einen Geburtstag?' }, { role: 'system', content: 'privacy' }]);
      }
    }
    setIsOpen(!isOpen);
  };

  const sendMessage = async () => {
    const msg = input.trim();
    if (!msg || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    trackEvent('chatbot_message_sent');

    try {
      const res = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, session_id: getSessionId() })
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else if (res.status === 429) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Bitte warten Sie einen Moment, bevor Sie weitere Nachrichten senden.' }]);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Entschuldigung, ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt.' }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Verbindungsfehler. Bitte versuchen Sie es erneut.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    trackEvent('chatbot_whatsapp_click');
    const phone = settings?.whatsappNumber || '491793912947';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent('Hallo, ich habe eine Frage zur BD Event Location.')}`;
    trackWhatsAppConversion(url);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/chat/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, guestCount: formData.guestCount ? parseInt(formData.guestCount) : null, session_id: getSessionId() })
      });
      if (res.ok) {
        trackEvent('chatbot_form_submit');
        setFormSent(true);
        setMessages(prev => [...prev, { role: 'assistant', content: 'Vielen Dank! Ihre Anfrage wurde gespeichert. Wir melden uns schnellstmoeglich bei Ihnen.' }]);
        setShowForm(false);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Die Anfrage konnte leider nicht gesendet werden. Bitte nutzen Sie unser Kontaktformular.' }]);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button className="chatbot-trigger" onClick={toggleOpen} data-testid="chatbot-trigger" aria-label="Chat oeffnen">
          <MessageCircle size={24} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="chatbot-window" data-testid="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">BD</div>
              <div>
                <div className="chatbot-header-title">BD Event Location</div>
                <div className="chatbot-header-status">Online - Sofortige Antwort</div>
              </div>
            </div>
            <button className="chatbot-close" onClick={toggleOpen} data-testid="chatbot-close"><X size={20} /></button>
          </div>

          <div className="chatbot-messages" data-testid="chatbot-messages">
            {messages.map((msg, i) => (
              msg.role === 'system' && msg.content === 'privacy' ? (
                <div key={i} className="chatbot-privacy-note" data-testid="chatbot-privacy-note">
                  KI-gestuetzter Assistent. Bitte keine sensiblen Daten eingeben. <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutz</a>
                </div>
              ) : msg.role !== 'system' ? (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.role}`}>
                {msg.role === 'assistant' && <div className="chatbot-msg-avatar">BD</div>}
                <div className="chatbot-msg-bubble">{msg.content}</div>
              </div>
              ) : null
            ))}
            {loading && (
              <div className="chatbot-msg chatbot-msg-assistant">
                <div className="chatbot-msg-avatar">BD</div>
                <div className="chatbot-msg-bubble chatbot-typing"><span /><span /><span /></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="chatbot-actions">
            <button className="chatbot-action-btn chatbot-action-wa" onClick={handleWhatsApp} data-testid="chatbot-whatsapp">
              <MessageCircle size={14} /> WhatsApp
            </button>
            {!formSent && (
              <button className="chatbot-action-btn chatbot-action-form" onClick={() => setShowForm(!showForm)} data-testid="chatbot-toggle-form">
                <ArrowRight size={14} /> {showForm ? 'Zurueck zum Chat' : 'Anfrage senden'}
              </button>
            )}
            <a href="tel:+491793912947" className="chatbot-action-btn chatbot-action-call" data-testid="chatbot-call">
              <Phone size={14} /> Anrufen
            </a>
          </div>

          {/* Inline Lead Form */}
          {showForm && !formSent && (
            <form className="chatbot-form" onSubmit={handleFormSubmit} data-testid="chatbot-lead-form">
              <div className="chatbot-form-row">
                <User size={14} />
                <input required placeholder="Name *" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              </div>
              <div className="chatbot-form-row">
                <Phone size={14} />
                <input required placeholder="Telefon *" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
              </div>
              <div className="chatbot-form-row">
                <Mail size={14} />
                <input required type="email" placeholder="E-Mail *" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
              </div>
              <div className="chatbot-form-row">
                <Calendar size={14} />
                <select value={formData.eventType} onChange={e => setFormData({...formData, eventType: e.target.value})}>
                  <option value="">Eventtyp waehlen</option>
                  <option value="Hochzeit">Hochzeit</option>
                  <option value="Firmenfeier">Firmenfeier</option>
                  <option value="Geburtstag">Geburtstag</option>
                  <option value="Sonstiges">Sonstiges</option>
                </select>
              </div>
              <div className="chatbot-form-row">
                <Calendar size={14} />
                <input type="date" placeholder="Wunschdatum" value={formData.eventDate} onChange={e => setFormData({...formData, eventDate: e.target.value})} />
              </div>
              <div className="chatbot-form-row">
                <Users size={14} />
                <input type="number" placeholder="Gaeste (ca.)" value={formData.guestCount} onChange={e => setFormData({...formData, guestCount: e.target.value})} />
              </div>
              <button type="submit" className="chatbot-form-submit" disabled={!formConsent} data-testid="chatbot-form-submit">Anfrage absenden</button>
              <label style={{display:'flex', gap:'6px', alignItems:'flex-start', fontSize:'10px', color:'#888', lineHeight:'1.4', marginTop:'4px', cursor:'pointer'}}>
                <input type="checkbox" checked={formConsent} onChange={e => setFormConsent(e.target.checked)} required style={{marginTop:'2px', flexShrink:0, accentColor:'#c8a96e'}} data-testid="chatbot-dsgvo-checkbox" />
                Ich stimme der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" style={{color:'#c8a96e'}}>Datenschutzerklaerung</a> zu.
              </label>
            </form>
          )}

          {/* Input */}
          {!showForm && (
            <div className="chatbot-input-area">
              <input
                ref={inputRef}
                className="chatbot-input"
                placeholder="Ihre Nachricht..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={500}
                data-testid="chatbot-input"
              />
              <button className="chatbot-send" onClick={sendMessage} disabled={!input.trim() || loading} data-testid="chatbot-send">
                <Send size={18} />
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Chatbot;
